import {forwardRef} from 'react';
import {useDispatch} from 'react-redux';

import * as S from './SimilarHotelCard.styles';
import {defaultTheme} from '@stencil/build-theme';

import {detailsPagePushGaThunk} from '@stencil/hotels-ga/src';
import {gaConfig} from '@stencil/hotels-config';

// Components
import CurrentHotelImage from '../CurrentHotelImage/CurrentHotelImage';
import SimilarHotelDetails from './SimilarHotelDetails/SimilarHotelDetails';
import SimilarHotelAmenities from './SimilarHotelAmenities/SimilarHotelAmenities';
import HotelPricingBlock from './SimilarHotelPricing/SimilarHotelPricing';

// Omniture
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics';
import {
  DetailsPageComponents,
  detailsClick,
} from '@stencil/omniture-analytics/src/constants/details';

//Types
import {SimilarHotelShape} from '@stencil/hotels-cg-reducer/src/details/types';

// import HotelCardPersuasionsBlock from '../../SRP/HotelCard/HotelCardPersuasionsBlock';

interface SimilarHotelCardProps {
  id: string;
  hotel: SimilarHotelShape;
  className: string;
  index?: number;
  hotelUrl?: string;
  detailsUrlForSimilarHotel?: string;
  similarPropertiesRankingAlgo?: string;
}
const SimilarHotelCard = forwardRef((props: SimilarHotelCardProps, ref) => {
  const {
    id,
    hotel,
    className,
    index = -1,
    hotelUrl,
    detailsUrlForSimilarHotel,
    similarPropertiesRankingAlgo,
  } = props;

  const isCurrentHotel = index === -1;
  const hotelLocation = hotel?.locationPersuasion?.[0] || hotel?.locationDetail?.name;

  const dispatch = useDispatch();

  const onCardClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();

    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${detailsClick.similarHotel.similarHotelClicked}_${index + 1}_dsp${
            similarPropertiesRankingAlgo ? `_rank_${similarPropertiesRankingAlgo}` : ''
          }`,
          type: omnitureConfig.ctaType.Button,
          componentName: DetailsPageComponents.SimilarProperties,
          destination: '',
        },
      }),
    );

    window.open(detailsUrlForSimilarHotel, '_blank', 'noreferrer');
  };

  return (
    <S.WrapperDivRight
      id={id}
      className={className}
      sticky={isCurrentHotel}
      // repeated value in different prop for semantic purposes
      currentHotel={isCurrentHotel}
      ref={ref}
    >
      <S.OuterWrapperSection
        {...(isCurrentHotel
          ? {
              backgroundColor: defaultTheme.colors.goYellow.light,
              boxShadow: '1rem 0 1.6rem 0 rgb(210, 210, 210)',
            }
          : {})}
      >
        <S.InnerWrapperWidth>
          <S.SimilarHotelWrapDiv>
            {isCurrentHotel ? (
              <span>Current Hotel</span>
            ) : (
              <S.SimilarHotelTextHidden>Similar Hotel</S.SimilarHotelTextHidden>
            )}
          </S.SimilarHotelWrapDiv>
          <S.SimilarHotelBorder
            currentHotel={isCurrentHotel}
            onClick={e => {
              if (index !== -1) {
                onCardClick(e);
              }
            }}
          >
            <CurrentHotelImage imageSrc={hotel?.hotelImage} />
            <SimilarHotelDetails
              hotelRating={hotel?.starRating}
              hotelName={hotel?.name}
              hotelLocation={hotelLocation}
              userRating={hotel?.giReviewData?.ratingData?.hotelRating}
              noOfReviews={hotel?.giReviewData?.ratingData?.reviewCount}
              isAltAcco={hotel?.isAltAcco}
              propertyType={hotel?.propertyType}
              detailPageUrl={hotelUrl}
              onClick={onCardClick}
              currentHotel={isCurrentHotel}
              index={index}
            />
            <SimilarHotelAmenities amenitiesList={hotel?.amenitiesList} />

            <S.SimilarHotelPricingWrapper>
              <HotelPricingBlock
                slashedPrice={hotel?.priceDetail?.price}
                showPrice={hotel?.priceDetail?.discountedPrice}
                totalRoomCount={hotel?.totalRoomCount}
              />
              <S.SelectButtomOuterWrap>
                {
                  // index -1 is explicityly
                  // deliberately passed for 'current hotel'
                  isCurrentHotel ? (
                    <S.SelectRoomButton
                      // @ts-ignore
                      backgroundColor="transparent"
                      color={defaultTheme.colors.downriver.base}
                    ></S.SelectRoomButton>
                  ) : (
                    <S.SelectRoomButton
                      onClick={e => {
                        dispatch(
                          detailsPagePushGaThunk({
                            event: gaConfig.HotelsDwebGenericEvent,
                            eventCategory: gaConfig.detailsPageClickEventCategoryName,
                            eventAction: gaConfig.DetailsGAClickEventActions.similarHotelsClicked,
                            eventLabel: hotel?.name,
                          }),
                        );

                        onCardClick(e);
                      }}
                    >
                      BOOK NOW
                    </S.SelectRoomButton>
                  )
                }
              </S.SelectButtomOuterWrap>
            </S.SimilarHotelPricingWrapper>
          </S.SimilarHotelBorder>
        </S.InnerWrapperWidth>
      </S.OuterWrapperSection>
    </S.WrapperDivRight>
  );
});

export default SimilarHotelCard;
